import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { clearSession, isSessionExpired } from "../services/auth";

const AdminLoginPage: React.FC = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Only skip the login screen for a session that is actually still live.
    // Checking mere presence would bounce an expired token straight back to the
    // dashboard, which bounces it back here — a redirect loop.
    if (!isSessionExpired()) {
      navigate("/dashboard");
      return;
    }

    // The token is dead (or absent); make sure no stale remnant survives.
    clearSession();

    // Explain an involuntary landing here, so an expired session doesn't look
    // like the app randomly logged them out.
    if (searchParams.get("session") === "expired") {
      setError("Your session has expired. Please sign in again.");
      return;
    }

    // Check for error from OAuth callback
    const authError = searchParams.get("error");
    if (authError) {
      setError(authError);
    }
  }, [navigate, searchParams]);

  const handleMicrosoftLogin = () => {
    // Redirect to backend Azure AD OAuth login endpoint
    window.location.href = "/api/admin/login";
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-merced-blue-50 via-white to-merced-gold-50 flex items-center justify-center p-4">
      {/* Thin government accent bar */}
      <div className="absolute top-0 left-0 h-1.5 w-full bg-gradient-to-r from-merced-blue-700 via-merced-blue-500 to-merced-gold" />

      <div className="max-w-md w-full animate-fade-in-up">
        {/* City of Merced Header */}
        <div className="text-center rounded-t-lg bg-white shadow-md border-b-4 border-merced-gold">
          <div className="flex flex-col items-center py-5 px-6">
            <img
              src="https://www.cityofmerced.gov/Project/Contents/Main/_gfx/cmn/logo.svg"
              alt="City of Merced Logo"
              className="h-16 w-auto transition-transform duration-300 hover:scale-105"
            />
          </div>
        </div>

        {/* Login Card */}
        <div className="card rounded-t-none">
          <div className="card-header">
            <h2 className="text-xl font-heading text-merced-blue-700">
              Admin Portal Login
            </h2>
            <p className="text-sm text-merced-gray-600 mt-1">
              Contract Document Management
            </p>
          </div>

          <div className="card-body">
            <div className="space-y-4">
              {error && (
                <div className="bg-merced-rust-50 border border-merced-rust-200 text-merced-rust-700 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <p className="text-sm text-merced-gray-600 text-center">
                Sign in with your City of Merced Microsoft account to access the
                admin portal.
              </p>

              <button
                type="button"
                onClick={handleMicrosoftLogin}
                className="w-full flex items-center justify-center gap-3 bg-white border border-merced-gray-300 rounded-md px-4 py-3 text-sm font-medium text-merced-gray-700 shadow-sm hover:bg-merced-gray-50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-merced-blue-500"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                >
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                Sign in with Microsoft
              </button>

              <button
                type="button"
                onClick={() => navigate("/")}
                className="w-full btn-outline py-2 mt-2"
              >
                Back to Public Search
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-merced-gray-600 mt-4">
          © {new Date().getFullYear()} City of Merced. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
